/**
 * 动画名称到友好显示的映射
 */
const ANIMATION_META = {
  Idle:       { label: '待机',    icon: '\u{1F9CD}', category: 'idle' },
  idle:       { label: '待机',    icon: '\u{1F9CD}', category: 'idle' },
  Walking:    { label: '走路',    icon: '\u{1F6B6}', category: 'move' },
  walking:    { label: '走路',    icon: '\u{1F6B6}', category: 'move' },
  Running:    { label: '跑步',    icon: '\u{1F3C3}', category: 'move' },
  running:    { label: '跑步',    icon: '\u{1F3C3}', category: 'move' },
  Dance:      { label: '跳舞',    icon: '\u{1F483}', category: 'dance' },
  dance:      { label: '跳舞',    icon: '\u{1F483}', category: 'dance' },
  Death:      { label: '倒地',    icon: '\u{1F480}', category: 'emotion' },
  death:      { label: '倒地',    icon: '\u{1F480}', category: 'emotion' },
  Jump:       { label: '跳跃',    icon: '⬆', category: 'action' },
  jump:       { label: '跳跃',    icon: '⬆', category: 'action' },
  Sitting:    { label: '坐下',    icon: '\u{1F4BA}', category: 'idle' },
  sitting:    { label: '坐下',    icon: '\u{1F4BA}', category: 'idle' },
  Wave:       { label: '挥手',    icon: '\u{1F44B}', category: 'emotion' },
  wave:       { label: '挥手',    icon: '\u{1F44B}', category: 'emotion' },
  Yes:        { label: '点头',    icon: '\u{2705}', category: 'emotion' },
  yes:        { label: '点头',    icon: '\u{2705}', category: 'emotion' },
  No:         { label: '摇头',    icon: '\u{274C}', category: 'emotion' },
  no:         { label: '摇头',    icon: '\u{274C}', category: 'emotion' },
  ThumbsUp:   { label: '点赞',    icon: '\u{1F44D}', category: 'emotion' },
  thumbsup:   { label: '点赞',    icon: '\u{1F44D}', category: 'emotion' },
  Punch:      { label: '出拳',    icon: '\u{1F44A}', category: 'action' },
  punch:      { label: '出拳',    icon: '\u{1F44A}', category: 'action' },
  Gesture:    { label: '手势',    icon: '\u{270B}', category: 'emotion' },
  gesture:    { label: '手势',    icon: '\u{270B}', category: 'emotion' },
  Standing:   { label: '站立',    icon: '\u{1F9CD}', category: 'idle' },
  standing:   { label: '站立',    icon: '\u{1F9CD}', category: 'idle' },
};

/** 根据动画名称获取显示信息（未知名称则返回默认值） */
function getAnimationMeta(name) {
  if (ANIMATION_META[name]) return ANIMATION_META[name];
  // 模糊匹配
  const lower = name.toLowerCase();
  for (const [key, meta] of Object.entries(ANIMATION_META)) {
    if (lower.includes(key.toLowerCase()) || key.toLowerCase().includes(lower)) {
      return meta;
    }
  }
  return { label: name, icon: '\u{1F3AC}', category: 'other' };
}

/** 类别中文 */
const CATEGORY_LABELS = {
  idle: '待机',
  move: '移动',
  dance: '舞蹈',
  emotion: '表情',
  action: '动作',
  other: '其他',
};

const CATEGORY_COLORS = {
  idle: '#4fc3f7',
  move: '#66bb6a',
  dance: '#ab47bc',
  emotion: '#ffa726',
  action: '#ef5350',
  other: '#78909c',
};

/**
 * 控制面板模块
 * 程序化创建 DOM，提供玻璃拟态风格的浮动控制面板
 * 包含动画控制、光照调节、模型调节、操作按钮、性能信息
 */
export class ControlPanel {
  /** @type {HTMLElement} */
  #panel;
  /** @type {HTMLElement | null} */
  #statusBar = null;
  /** @type {Record<string, Function>} */
  #callbacks;

  /**
   * @param {object} callbacks - UI 事件回调函数
   */
  constructor(callbacks = {}) {
    this.#callbacks = {
      onPlayAnimation: () => {},
      onPauseAnimation: () => {},
      onStopAnimation: () => {},
      onSetAnimationSpeed: () => {},
      onSetAmbientIntensity: () => {},
      onSetMainLightIntensity: () => {},
      onSetExposure: () => {},
      onSetModelScale: () => {},
      onSetRotationSpeed: () => {},
      onSetRoughness: () => {},
      onSetMetalness: () => {},
      onResetCamera: () => {},
      onScreenshot: () => {},
      onPreset: () => {},
      onTogglePanel: () => {},
      ...callbacks,
    };
    this.#create();
    this.#bindEvents();
  }

  /** 创建面板 DOM 结构 */
  #create() {
    const panel = document.createElement('div');
    panel.id = 'control-panel';
    panel.innerHTML = `
      <!-- 面板主体 -->
      <div class="panel-body" id="panel-body">
        <!-- 面板头部 -->
        <div class="panel-header">
          <span class="panel-title">控制面板</span>
        </div>

        <!-- 动画控制 -->
        <div class="panel-section">
          <div class="section-header" data-section="animation">
            <span class="section-icon">&#9662;</span>
            <span>动画控制</span>
          </div>
          <div class="section-content" id="section-animation">
            <!-- 动画卡片网格（动态填充） -->
            <div class="anim-grid" id="anim-grid">
              <div class="anim-empty">加载模型后显示动画</div>
            </div>
            <!-- 播放控制 -->
            <div class="anim-controls">
              <div class="slider-group">
                <label>速度 <span class="slider-value" id="speed-value">1.0x</span></label>
                <input type="range" id="speed-slider" class="panel-slider" min="0.1" max="3.0" step="0.1" value="1.0">
              </div>
              <div class="button-row">
                <button class="panel-btn" id="btn-play" title="播放">&#9654; 播放</button>
                <button class="panel-btn" id="btn-pause" title="暂停">&#9208; 暂停</button>
                <button class="panel-btn" id="btn-stop" title="停止">&#9209; 停止</button>
              </div>
            </div>
          </div>
        </div>

        <!-- 光照调节 -->
        <div class="panel-section">
          <div class="section-header" data-section="lighting">
            <span class="section-icon collapsed">&#9662;</span>
            <span>光照调节</span>
          </div>
          <div class="section-content collapsed" id="section-lighting">
            <div class="slider-group">
              <label>环境光 <span class="slider-value" id="ambient-value">0.6</span></label>
              <input type="range" id="ambient-slider" class="panel-slider" min="0.0" max="2.0" step="0.1" value="0.6">
            </div>
            <div class="slider-group">
              <label>主光源 <span class="slider-value" id="main-light-value">1.5</span></label>
              <input type="range" id="main-light-slider" class="panel-slider" min="0.0" max="3.0" step="0.1" value="1.5">
            </div>
            <div class="slider-group">
              <label>曝光度 <span class="slider-value" id="exposure-value">1.2</span></label>
              <input type="range" id="exposure-slider" class="panel-slider" min="0.3" max="2.5" step="0.1" value="1.2">
            </div>
          </div>
        </div>

        <!-- 模型调节 -->
        <div class="panel-section">
          <div class="section-header" data-section="model">
            <span class="section-icon collapsed">&#9662;</span>
            <span>模型调节</span>
          </div>
          <div class="section-content collapsed" id="section-model">
            <div class="slider-group">
              <label>缩放 <span class="slider-value" id="scale-value">1.0</span></label>
              <input type="range" id="scale-slider" class="panel-slider" min="0.3" max="3.0" step="0.1" value="1.0">
            </div>
            <div class="slider-group">
              <label>自动旋转 <span class="slider-value" id="rotation-value">0.0</span></label>
              <input type="range" id="rotation-slider" class="panel-slider" min="0.0" max="2.0" step="0.1" value="0.0">
            </div>
            <div class="slider-group">
              <label>粗糙度 <span class="slider-value" id="roughness-value">默认</span></label>
              <input type="range" id="roughness-slider" class="panel-slider" min="0.0" max="1.0" step="0.05" value="0.65">
            </div>
            <div class="slider-group">
              <label>金属度 <span class="slider-value" id="metalness-value">默认</span></label>
              <input type="range" id="metalness-slider" class="panel-slider" min="0.0" max="1.0" step="0.05" value="0.0">
            </div>
            <div class="preset-row">
              <button class="preset-btn" data-preset="default">默认</button>
              <button class="preset-btn" data-preset="smooth">光滑</button>
              <button class="preset-btn" data-preset="metallic">金属</button>
              <button class="preset-btn" data-preset="matte">磨砂</button>
            </div>
          </div>
        </div>

      </div>
      <button class="cp-slide-toggle" id="cp-slide-toggle" title="展开">&#9664;</button>
    `;

    document.body.appendChild(panel);
    this.#panel = panel;

    // 创建底部状态栏
    this.#createStatusBar();
  }

  /** 创建底部性能状态栏 */
  #createStatusBar() {
    const bar = document.createElement('div');
    bar.id = 'status-bar';
    bar.innerHTML = `
      <button class="status-btn" id="btn-reset-camera" title="重置相机">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
        重置相机
      </button>
      <button class="status-btn" id="btn-screenshot" title="截图保存">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
        截图保存
      </button>
      <div class="status-info">
        <span class="status-item">FPS <span class="status-value" id="fps-value">--</span></span>
        <span class="status-divider"></span>
        <span class="status-item">三角形 <span class="status-value" id="tri-count">--</span></span>
        <span class="status-divider"></span>
        <span class="status-item" id="status-anim-label">动画 <span class="status-value" id="status-anim">--</span></span>
      </div>
    `;
    document.body.appendChild(bar);
    this.#statusBar = bar;
  }

  /** 绑定所有 UI 事件 */
  #bindEvents() {
    // 面板折叠/展开（向右滑动隐藏）
    this.#panel.querySelector('#cp-slide-toggle')?.addEventListener('click', () => {
      const isCollapsed = this.#panel.classList.toggle('collapsed');
      const btn = this.#panel.querySelector('#cp-slide-toggle');
      if (btn) {
        btn.innerHTML = isCollapsed ? '&#9664;' : '&#9654;';
      }
    });

    // 手风琴折叠/展开各区块
    this.#panel.querySelectorAll('.section-header').forEach((header) => {
      header.addEventListener('click', () => {
        const content = header.nextElementSibling;
        if (!content || !content.classList.contains('section-content')) return;
        const isCollapsed = content.classList.toggle('collapsed');
        const icon = header.querySelector('.section-icon');
        if (icon) icon.classList.toggle('collapsed', isCollapsed);
      });
    });

    // 动画卡片点击（事件委托）
    this.#panel.querySelector('#anim-grid')?.addEventListener('click', (e) => {
      const card = e.target.closest('.anim-card');
      if (!card) return;
      const name = card.dataset.anim;
      if (name) this.#callbacks.onPlayAnimation(name);
    });

    // 播放/暂停/停止
    this.#panel.querySelector('#btn-play')?.addEventListener('click', () => {
      const active = this.#panel.querySelector('.anim-card.active');
      if (active) this.#callbacks.onPlayAnimation(active.dataset.anim);
    });
    this.#panel.querySelector('#btn-pause')?.addEventListener('click', () => {
      this.#callbacks.onPauseAnimation();
    });
    this.#panel.querySelector('#btn-stop')?.addEventListener('click', () => {
      this.#callbacks.onStopAnimation();
    });

    // 动画速度滑块
    this.#bindSlider('speed-slider', 'speed-value', this.#callbacks.onSetAnimationSpeed, (v) => `${v}x`);

    // 光照滑块
    this.#bindSlider('ambient-slider', 'ambient-value', this.#callbacks.onSetAmbientIntensity);
    this.#bindSlider('main-light-slider', 'main-light-value', this.#callbacks.onSetMainLightIntensity);
    this.#bindSlider('exposure-slider', 'exposure-value', this.#callbacks.onSetExposure);

    // 模型调节滑块
    this.#bindSlider('scale-slider', 'scale-value', this.#callbacks.onSetModelScale);
    this.#bindSlider('rotation-slider', 'rotation-value', this.#callbacks.onSetRotationSpeed);
    this.#bindSlider('roughness-slider', 'roughness-value', this.#callbacks.onSetRoughness, (v) => v === 0.65 ? '默认' : v);
    this.#bindSlider('metalness-slider', 'metalness-value', this.#callbacks.onSetMetalness, (v) => v === 0 ? '默认' : v);

    // 材质预设按钮
    this.#panel.querySelectorAll('.preset-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.#callbacks.onPreset(btn.dataset.preset);
      });
    });

    // 操作按钮
    this.#statusBar.querySelector('#btn-reset-camera')?.addEventListener('click', () => {
      this.#callbacks.onResetCamera();
    });
    this.#statusBar.querySelector('#btn-screenshot')?.addEventListener('click', () => {
      this.#callbacks.onScreenshot();
    });
  }

  /**
   * 通用滑块绑定辅助方法
   */
  #bindSlider(sliderId, valueId, callback, formatter) {
    const slider = this.#panel.querySelector(`#${sliderId}`);
    const value = this.#panel.querySelector(`#${valueId}`);
    if (!slider || !callback) return;

    slider.addEventListener('input', () => {
      const val = parseFloat(slider.value);
      if (value) value.textContent = formatter ? formatter(val) : val;
      callback(val);
    });
  }

  /**
   * 填充动画卡片网格
   * @param {Array<{name: string, duration: number, tracks: number}>} details
   */
  populateAnimations(details) {
    const grid = this.#panel.querySelector('#anim-grid');
    if (!grid) return;
    grid.innerHTML = '';

    if (details.length === 0) {
      grid.innerHTML = '<div class="anim-empty">该模型没有动画</div>';
      return;
    }

    for (const { name, duration, tracks } of details) {
      const meta = getAnimationMeta(name);
      const card = document.createElement('div');
      card.className = 'anim-card';
      card.dataset.anim = name;

      const durMin = Math.floor(duration / 60);
      const durSec = (duration % 60).toFixed(0).padStart(2, '0');
      const timeStr = durMin > 0 ? `${durMin}:${durSec}` : `${durSec}s`;
      const catColor = CATEGORY_COLORS[meta.category] || CATEGORY_COLORS.other;

      card.innerHTML = `
        <div class="anim-card-icon" style="color: ${catColor}">${meta.icon}</div>
        <div class="anim-card-label">${meta.label}</div>
        <span class="anim-card-badge" style="border-color: ${catColor}30; color: ${catColor}">${CATEGORY_LABELS[meta.category] || '其他'}</span>
        <div class="anim-card-time">${timeStr}</div>
      `;
      grid.appendChild(card);
    }
  }

  /** 更新 FPS 显示 */
  updateFPS(fps) {
    const el = this.#statusBar?.querySelector('#fps-value');
    if (el) el.textContent = fps;
  }

  /** 更新三角形数量 */
  updateTriangleCount(count) {
    const el = this.#statusBar?.querySelector('#tri-count');
    if (el) el.textContent = count.toLocaleString();
  }

  /** 更新当前动画名称 */
  setActiveAnimation(name) {
    this.#panel.querySelectorAll('.anim-card').forEach((card) => {
      card.classList.toggle('active', card.dataset.anim === name);
    });
    const el = this.#statusBar?.querySelector('#status-anim');
    if (el) el.textContent = name;
  }

  /** 完全销毁面板（移除 DOM） */
  dispose() {
    this.#panel?.remove();
    this.#panel = null;
    this.#statusBar?.remove();
    this.#statusBar = null;
  }
}
